import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip,
  Snackbar,
} from '@mui/material';
import { Add, Search, Edit, Assignment, Person, AutoAwesome, People } from '@mui/icons-material';
import ArchiveIconSvg from '../components/common/ArchiveIconSvg';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientService, ClientFilters } from '../services/clientService';
import { projectService } from '../services/projectService';
import { Client } from '../types';
import StatusChip from '../components/common/StatusChip';
import ClientForm from '../components/clients/ClientForm';
import { useAuth } from '../contexts/AuthContext';
import { useProjectWorkflow } from '../contexts/ProjectWorkflowContext';
import ClientAssignmentManager from '../components/clientAssignments/ClientAssignmentManager';
import {
  notifyClientCreated,
  refetchWorkflowPanelQueries,
  completeClientDeletion,
  completeClientUpdate,
} from '../utils/workflowPanelCache';
import ClientDetailPage from './ClientDetailPage';

const ClientsListPage: React.FC = () => {
  const { user, isSuperAdmin, isAgency, isAdmin } = useAuth();
  const {
    focusClientInPanel,
    clearProject,
    selectedClient: workflowClient,
    selectedProject,
    patchClientInSelection,
  } = useProjectWorkflow();
  const navigate = useNavigate();
  const canManageClients = isSuperAdmin || isAgency;
  const tableColSpan = isSuperAdmin ? 11 : canManageClients ? 9 : 8;
  const [filters, setFilters] = useState<ClientFilters>({
    page: 0,
    limit: 25,
    search: '',
    status: undefined,
  });
  const [formOpen, setFormOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | undefined>();
  const [assignmentManagerOpen, setAssignmentManagerOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [clientForUser, setClientForUser] = useState<Client | undefined>();
  const [userForm, setUserForm] = useState({ email: '', password: '', firstName: '', lastName: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', filters, isAgency],
    queryFn: () =>
      clientService.getClients({
        ...filters,
        limit: 1000,
        ...(isAgency ? { excludeAgencySelfProfile: true } : {}),
      }),
  });

  const { data: agencyProjectsData } = useQuery({
    queryKey: ['wf-agency-project-count', 'clients-page'],
    queryFn: () => projectService.getProjects({ limit: 1 }),
    enabled: isAgency,
  });

  const createMutation = useMutation({
    mutationFn: clientService.createClient,
    onSuccess: async (created) => {
      notifyClientCreated(queryClient, created, focusClientInPanel);
      await refetchWorkflowPanelQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      setFormOpen(false);
      if (isAgency || isSuperAdmin) {
        navigate(`/clients/${created.id}`, { state: { promptFirstProject: true } });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      clientService.updateClient(id, data),
    onSuccess: (updated) => {
      completeClientUpdate(queryClient, updated, { patchClientInSelection });
      setFormOpen(false);
      setSelectedClient(undefined);
      setSnackbar({ open: true, message: `Client '${updated.name}' updated successfully.` });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: clientService.deleteClient,
    onSuccess: (_data, clientId) => {
      completeClientDeletion(queryClient, clientId, {
        clearProject,
        selectedClientId: workflowClient?.id,
        selectedProjectClientId: selectedProject?.clientId,
        onCleared: () => navigate('/dashboard'),
      });
      setDeleteDialogOpen(false);
      setClientToDelete(undefined);
      setSnackbar({ open: true, message: 'Client archived successfully.' });
    },
    onError: (error: any) => {
      console.error('Delete client error:', error);
    },
  });

  const createUserMutation = useMutation({
    mutationFn: ({ clientId, data }: { clientId: string; data: typeof userForm }) =>
      clientService.createClientUser(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setUserDialogOpen(false);
      setClientForUser(undefined);
      setUserForm({ email: '', password: '', firstName: '', lastName: '' });
      setSnackbar({ open: true, message: 'Client login user created successfully.' });
    },
  });

  const handleFilterChange = (field: keyof ClientFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 0 : value, // Reset page when other filters change
    }));
  };

  const handleFormSubmit = async (formData: any) => {
    if (selectedClient) {
      await updateMutation.mutateAsync({
        id: selectedClient.id,
        data: formData,
      });
    } else {
      const created = await createMutation.mutateAsync(formData);
      const withLogin = formData.createLogin;
      const clientName = created.name || 'Client';
      setSnackbar({
        open: true,
        message: withLogin
          ? `Client '${clientName}' created with login — share credentials with your client.`
          : `A new client '${clientName}' has been created.`,
      });
    }
  };

  const handleDelete = async () => {
    if (clientToDelete) {
      try {
        await deleteMutation.mutateAsync(clientToDelete.id);
      } catch (error) {
        // Error is handled by the mutation's onError callback
        console.error('Delete failed:', error);
      }
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedClient(undefined);
  };

  const clients = data?.data ?? [];
  const agencyHasProjects =
    (agencyProjectsData?.pagination?.total ?? agencyProjectsData?.data?.length ?? 0) > 0;
  const showAgencyOnboarding = isAgency && !isLoading && clients.length === 0 && !agencyHasProjects;

  // Helper function to get client initials and color
  const getClientAvatar = (name: string, index: number) => {
    const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#f43f5e', '#06b6d4'];
    const initial = name?.charAt(0).toUpperCase() || '?';
    const colorClass = colors[index % colors.length];
    return { initial, colorClass };
  };

  if (showAgencyOnboarding) {
    return (
      <Box sx={{ p: { xs: 2, md: 3.5 } }}>
        <Box
          className="wf-client-gate"
          sx={{ border: '1px dashed var(--border)', borderRadius: '16px', background: 'var(--surface)' }}
        >
          <People sx={{ fontSize: 48, color: 'var(--accent)', mb: 1, opacity: 0.85 }} />
          <h2>Create your first client</h2>
          <p>
            Add a client to your agency account. Next, you&apos;ll create a project and start the link-building workflow.
          </p>
          <button
            type="button"
            className="wf-btn-create wf-btn-create-xl"
            onClick={() => {
              setSelectedClient(undefined);
              setFormOpen(true);
            }}
          >
            <AutoAwesome sx={{ fontSize: 22 }} />
            Create Client
          </button>
        </Box>
        <ClientForm
          open={formOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          client={selectedClient}
          loading={createMutation.isPending || updateMutation.isPending}
          userRole={user?.role || 'ADMIN'}
          canCreateLogin={isSuperAdmin}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>
            Clients
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Manage your agency clients and assignments
          </Typography>
        </Box>
        {canManageClients && (
          <Box sx={{ display: 'flex', gap: '10px' }}>
            {isSuperAdmin && (
            <Button
              variant="outlined"
              startIcon={<Assignment sx={{ fontSize: '14px' }} />}
              onClick={() => setAssignmentManagerOpen(true)}
              sx={{
                padding: '8px 16px',
                borderRadius: 'var(--radius-sm)',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
                backgroundColor: 'var(--surface)',
                '&:hover': {
                  backgroundColor: 'var(--bg)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border)'
                }
              }}
            >
              Manage Assignments
            </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Add sx={{ fontSize: '14px' }} />}
              onClick={() => {
                setSelectedClient(undefined);
                setFormOpen(true);
              }}
              sx={{
                padding: '8px 16px',
                borderRadius: 'var(--radius-sm)',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                backgroundColor: 'var(--accent)',
                color: 'white',
                boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
                '&:hover': {
                  backgroundColor: 'var(--accent-hover)',
                  boxShadow: 'var(--shadow-md)'
                }
              }}
            >
              Add Client
            </Button>
          </Box>
        )}
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <TextField
          placeholder="Search clients…"
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: '14px', color: 'var(--text-muted)' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '280px',
            '& .MuiOutlinedInput-root': {
              padding: '8px 14px',
              fontSize: '13px',
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              boxShadow: 'var(--shadow)',
              '&:hover': {
                borderColor: 'var(--border)'
              },
              '&.Mui-focused': {
                borderColor: 'var(--accent)',
                boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
              },
              '& fieldset': {
                border: 'none'
              }
            },
            '& .MuiOutlinedInput-input': {
              padding: 0,
              fontSize: '13px',
              color: 'var(--text-primary)'
            }
          }}
        />
        <FormControl sx={{ minWidth: '160px' }}>
          <Select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
            displayEmpty
            sx={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              boxShadow: 'var(--shadow)',
              height: '38px',
              '& .MuiSelect-select': {
                padding: '8px 14px',
                paddingRight: '32px'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
          >
            <MenuItem value="" sx={{ fontSize: '13px' }}>All Statuses</MenuItem>
            <MenuItem value="ACTIVE" sx={{ fontSize: '13px' }}>Active</MenuItem>
            <MenuItem value="INACTIVE" sx={{ fontSize: '13px' }}>Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load clients. Please try again.
        </Alert>
      )}

      {/* Data Table */}
      <Paper sx={{ 
        width: '100%', 
        overflow: 'hidden',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow)',
        animation: 'fadeUp 0.35s ease forwards'
      }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Name</TableCell>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Email</TableCell>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Phone</TableCell>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Company</TableCell>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Country</TableCell>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Status</TableCell>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>User Account</TableCell>
                {isSuperAdmin && <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Budget</TableCell>}
                {isSuperAdmin && <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Created By</TableCell>}
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Created</TableCell>
                {canManageClients && <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={tableColSpan} align="center" sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)' }}>Loading...</TableCell>
                </TableRow>
              ) : (data?.data || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableColSpan} align="center" sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)' }}>
                    {isAdmin && !isSuperAdmin
                      ? 'No clients assigned to you. Contact your Super Admin to get client access.'
                      : 'No clients found'}
                  </TableCell>
                </TableRow>
              ) : (
                (data?.data || [])
                  .slice((filters.page || 0) * (filters.limit || 25), ((filters.page || 0) + 1) * (filters.limit || 25))
                  .map((client: Client, index: number) => {
                  const { initial, colorClass } = getClientAvatar(client.name, index);
                  return (
                    <TableRow 
                      hover 
                      key={client.id}
                      onClick={() => {
                        if (canManageClients) navigate(`/clients/${client.id}`);
                      }}
                      sx={{
                        cursor: canManageClients ? 'pointer' : 'default',
                        transition: 'background 0.1s',
                        '&:hover': {
                          background: canManageClients ? '#fafbff' : undefined,
                        },
                      }}
                    >
                      <TableCell sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
                          <Box sx={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10.5px',
                            fontWeight: 600,
                            flexShrink: 0,
                            background: colorClass,
                            color: 'white',
                            marginRight: '8px'
                          }}>
                            {initial}
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: canManageClients ? 'var(--accent)' : 'var(--text-primary)',
                              fontSize: '10.5px',
                            }}
                          >
                            {client.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ padding: '13px 18px', fontSize: '10.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle', color: 'var(--text-muted)' }}>
                        {client.email || '—'}
                      </TableCell>
                      <TableCell sx={{ padding: '13px 18px', fontSize: '10.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle', color: 'var(--text-muted)', fontFamily: 'DM Mono, monospace' }}>
                        {(client as any).phone || '—'}
                      </TableCell>
                      <TableCell sx={{ padding: '13px 18px', fontSize: '10.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                        {(client as any).company || '—'}
                      </TableCell>
                      <TableCell sx={{ padding: '13px 18px', fontSize: '10.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                        {(client as any).country || '—'}
                      </TableCell>
                      <TableCell sx={{ padding: '13px 18px', fontSize: '10.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '3px 10px',
                          borderRadius: '99px',
                          fontSize: '11.5px',
                          fontWeight: 600,
                          background: (client as any).isActive ? 'var(--green-soft)' : 'var(--surface2)',
                          color: (client as any).isActive ? 'var(--green)' : 'var(--text-muted)',
                          border: (client as any).isActive ? '1px solid #bbf7d0' : '1px solid var(--border)',
                          '&::before': {
                            content: '""',
                            width: '5px',
                            height: '5px',
                            borderRadius: '50%',
                            flexShrink: 0,
                            background: (client as any).isActive ? 'var(--green)' : 'var(--text-muted)'
                          }
                        }}>
                          {(client as any).isActive ? 'Active' : 'Inactive'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ padding: '13px 18px', fontSize: '10.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                        {(client as any).users && (client as any).users.length > 0 ? (
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '3px 10px',
                            borderRadius: '99px',
                            fontSize: '10.5px',
                            fontWeight: 600,
                            background: 'var(--green-soft)',
                            color: 'var(--green)',
                            border: '1px solid #bbf7d0',
                            '&::before': {
                              content: '""',
                              width: '5px',
                              height: '5px',
                              borderRadius: '50%',
                              flexShrink: 0,
                              background: 'var(--green)'
                            }
                          }}>
                            Connected
                          </Box>
                        ) : (
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '3px 10px',
                            borderRadius: '99px',
                            fontSize: '10.5px',
                            fontWeight: 600,
                            background: 'var(--surface2)',
                            color: 'var(--text-muted)',
                            border: '1px solid var(--border)',
                            '&::before': {
                              content: '""',
                              width: '5px',
                              height: '5px',
                              borderRadius: '50%',
                              flexShrink: 0,
                              background: 'var(--text-muted)'
                            }
                          }}>
                            No Account
                          </Box>
                        )}
                      </TableCell>
                      {isSuperAdmin && <TableCell sx={{ padding: '13px 18px', fontSize: '10.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle', color: 'var(--text-muted)' }}>
                        {(client as any).budget ? `$${Number((client as any).budget).toFixed(0)}` : '—'}
                      </TableCell>}
                      {isSuperAdmin && <TableCell sx={{ padding: '13px 18px', fontSize: '10.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle', color: 'var(--text-muted)' }}>
                        {(client as any).createdBy ? `${(client as any).createdBy.firstName} ${(client as any).createdBy.lastName}` : 'N/A'}
                      </TableCell>}
                      <TableCell sx={{ padding: '13px 18px', fontSize: '10.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle', color: 'var(--text-muted)', fontFamily: 'DM Mono, monospace' }}>
                        {new Date(client.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </TableCell>
                      {canManageClients && (
                        <TableCell
                          sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {isSuperAdmin && (
                              <IconButton
                                size="small"
                                title="Add client user"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setClientForUser(client);
                                  setUserForm({
                                    firstName: client.name || '',
                                    lastName: '',
                                    email: client.email || '',
                                    password: '',
                                  });
                                  setUserDialogOpen(true);
                                }}
                                sx={{ width: '30px', height: '30px', borderRadius: '6px' }}
                              >
                                <Person sx={{ fontSize: '14px', color: 'var(--text-muted)' }} />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedClient(client);
                                setFormOpen(true);
                              }}
                              sx={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '6px',
                                border: 'none',
                                background: 'none',
                                transition: 'all 0.15s',
                                '&:hover': {
                                  background: 'var(--accent-soft)',
                                  '& svg': {
                                    color: 'var(--accent)'
                                  }
                                }
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </IconButton>
                            <Tooltip title="Archive Client">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setClientToDelete(client);
                                setDeleteDialogOpen(true);
                              }}
                              sx={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '6px',
                                border: 'none',
                                background: 'none',
                                transition: 'all 0.15s',
                                '&:hover': {
                                  background: 'var(--amber-soft)',
                                  '& svg': {
                                    color: 'var(--amber)'
                                  }
                                }
                              }}
                            >
                              <ArchiveIconSvg />
                            </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '14px 18px',
          borderTop: '1px solid var(--border-light)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
            Rows per page:
            <select 
              value={filters.limit || 25}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
              style={{
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                borderRadius: '6px',
                padding: '3px 6px',
                fontFamily: 'inherit',
                fontSize: '12px',
                outline: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={1000}>1000</option>
            </select>
          </Box>
          <Typography sx={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
            {data?.pagination?.total ? `${(filters.page || 0) * (filters.limit || 25) + 1}–${Math.min(((filters.page || 0) + 1) * (filters.limit || 25), data.pagination.total)} of ${data.pagination.total}` : `${(filters.page || 0) * (filters.limit || 25) + 1}–${((filters.page || 0) + 1) * (filters.limit || 25)} of ${(data?.data || []).length}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: '4px' }}>
            <IconButton
              onClick={() => handleFilterChange('page', (filters.page || 0) - 1)}
              disabled={(filters.page || 0) === 0}
              sx={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                '&:hover': {
                  background: 'var(--bg)'
                },
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px', color: 'var(--text-secondary)' }}>
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </IconButton>
            <IconButton
              onClick={() => handleFilterChange('page', (filters.page || 0) + 1)}
              disabled={!data?.data || ((filters.page || 0) + 1) * (filters.limit || 25) >= (data?.data || []).length}
              sx={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                '&:hover': {
                  background: 'var(--bg)'
                },
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px', color: 'var(--text-secondary)' }}>
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Client Form Dialog */}
      <ClientForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        client={selectedClient}
        loading={createMutation.isPending || updateMutation.isPending}
        userRole={user?.role || 'ADMIN'}
        canCreateLogin={isSuperAdmin}
      />

      {/* Archive confirmation dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => {
          setDeleteDialogOpen(false);
          deleteMutation.reset();
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid var(--border)',
            maxWidth: '440px',
            width: '100%'
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '22px 26px 18px',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <Box>
            <DialogTitle sx={{ padding: 0, fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.3px' }}>
              Archive Client
            </DialogTitle>
            <Box sx={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Hidden from lists — restore from Settings → Restore Archived Data
            </Box>
          </Box>
          <IconButton
            onClick={() => {
              setDeleteDialogOpen(false);
              deleteMutation.reset();
            }}
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '6px',
              '&:hover': {
                background: 'var(--bg)'
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }}>
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </IconButton>
        </Box>
        <DialogContent sx={{ padding: '22px 26px' }}>
          <Box sx={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
            <Box sx={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'var(--amber-soft)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', color: 'var(--amber)' }}>
                <polyline points="21 8 21 21 3 21 3 8" />
                <rect x="1" y="3" width="22" height="5" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
            </Box>
            <Box sx={{ flex: 1 }}>
              <DialogContentText sx={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                Archive client <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>"{clientToDelete?.name}"</strong>? Active projects under this client will also be archived. Restore within 7 days from <strong>Settings → Restore Archived Data</strong>.
              </DialogContentText>
            </Box>
          </Box>
          {deleteMutation.error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                borderRadius: 'var(--radius-sm)',
                fontSize: '12.5px',
                '& .MuiAlert-icon': {
                  fontSize: '18px'
                }
              }}
            >
              {deleteMutation.error?.response?.data?.error || 'Failed to archive client. Please try again.'}
            </Alert>
          )}
        </DialogContent>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '10px',
          padding: '16px 26px',
          borderTop: '1px solid var(--border-light)'
        }}>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              deleteMutation.reset();
            }}
            disabled={deleteMutation.isPending}
            sx={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)',
              backgroundColor: 'var(--surface)',
              '&:hover': {
                backgroundColor: 'var(--bg)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={deleteMutation.isPending}
            startIcon={<ArchiveIconSvg size={14} />}
            sx={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              backgroundColor: 'var(--amber)',
              color: 'white',
              boxShadow: '0 1px 2px rgba(217,119,6,0.2)',
              '&:hover': {
                backgroundColor: '#b45309',
                boxShadow: 'var(--shadow-md)'
              },
              '&:disabled': {
                backgroundColor: 'var(--amber)',
                opacity: 0.6
              }
            }}
          >
            {deleteMutation.isPending ? 'Archiving...' : 'Archive Client'}
          </Button>
        </Box>
      </Dialog>

      {/* Create client login user */}
      <Dialog
        open={userDialogOpen}
        onClose={() => {
          setUserDialogOpen(false);
          setClientForUser(undefined);
          setUserForm({ email: '', password: '', firstName: '', lastName: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Client User — {clientForUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="First Name" value={userForm.firstName} onChange={(e) => setUserForm((f) => ({ ...f, firstName: e.target.value }))} required fullWidth />
            <TextField label="Last Name" value={userForm.lastName} onChange={(e) => setUserForm((f) => ({ ...f, lastName: e.target.value }))} fullWidth />
            <TextField label="Email" type="email" value={userForm.email} onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))} required fullWidth />
            <TextField label="Password" type="password" value={userForm.password} onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))} required fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUserDialogOpen(false);
              setClientForUser(undefined);
              setUserForm({ email: '', password: '', firstName: '', lastName: '' });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={createUserMutation.isPending || !userForm.email || !userForm.password || !userForm.firstName}
            onClick={() => clientForUser && createUserMutation.mutate({ clientId: clientForUser.id, data: userForm })}
          >
            {createUserMutation.isPending ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Assignment Manager */}
      <ClientAssignmentManager
        open={assignmentManagerOpen}
        onClose={() => setAssignmentManagerOpen(false)}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ open: false, message: '' })} severity="success" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ClientsPage: React.FC = () => (
  <Routes>
    <Route index element={<ClientsListPage />} />
    <Route path=":clientId" element={<ClientDetailPage />} />
  </Routes>
);

export default ClientsPage;
